import { ArrowRight, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
  AlcatrazSection,
  CtaSection,
  GlanceSection,
  IntroSection,
  ProductConfig,
  SpecsSection,
} from "../product-types";
import { SectionShell } from "../shared/primitives";
import { reveal } from "../shared/reveal";

export function Breadcrumb({ breadcrumb }: { breadcrumb: ProductConfig["breadcrumb"] }) {
  return (
    <div className="border-b border-border/60 bg-background/60">
      <nav
        aria-label={breadcrumb.ariaLabel}
        className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
      >
        <a href={breadcrumb.homeHref} className="hover:text-foreground">
          {breadcrumb.homeLabel}
        </a>
        <ChevronRight aria-hidden className="h-3 w-3" />
        <a href={breadcrumb.productsHref} className="hover:text-foreground">
          {breadcrumb.productsLabel}
        </a>
        <ChevronRight aria-hidden className="h-3 w-3" />
        <span className="text-foreground">{breadcrumb.productName}</span>
      </nav>
    </div>
  );
}

export function GlanceStats({ section }: { section: GlanceSection }) {
  return (
    <SectionShell bgClassName={section.bgClassName}>
      <div className="mx-auto max-w-7xl px-6">
        <dl className="grid grid-cols-1 divide-y divide-border/60 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
          {section.stats.map(([value, label], i) => (
            <div
              key={label}
              className="animate-fade-up px-2 py-6 text-center sm:px-6 sm:py-7"
              style={reveal((i + 1) * 60)}
            >
              <dt className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">
                {value}
              </dt>
              <dd className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                {label}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </SectionShell>
  );
}

export function ProductIntro({ section }: { section: IntroSection }) {
  return (
    <SectionShell>
      <div className="mx-auto max-w-3xl px-6 py-24 text-center animate-fade-up">
        <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
          inext <span className="text-gradient">{section.name}</span>
        </h2>
        {section.paras.map((para, i) => (
          <p
            key={para}
            className={
              i === 0
                ? "mt-6 text-lg leading-relaxed text-muted-foreground"
                : "mt-4 text-base leading-relaxed text-muted-foreground"
            }
          >
            {para}
          </p>
        ))}
      </div>
    </SectionShell>
  );
}

export function AlcatrazBand({ section }: { section: AlcatrazSection }) {
  return (
    <SectionShell>
      <div className="mx-auto max-w-7xl px-6 py-20">
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-primary/10 p-8 animate-fade-up sm:p-12">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-px -z-10 rounded-3xl bg-gradient-to-tr from-primary/30 via-transparent to-primary/10 opacity-70 blur-2xl"
          />
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-background/60 px-3 py-1 text-xs text-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                {section.pill}
              </div>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl">
                {section.title}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{section.body}</p>
            </div>
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="h-11 rounded-lg px-6 font-medium"
            >
              <a href={section.ctaHref}>
                {section.ctaLabel}
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

export function SpecsTable({ section }: { section: SpecsSection }) {
  return (
    <SectionShell bgClassName={section.bgClassName}>
      <div className="mx-auto max-w-5xl px-6 py-24 animate-fade-up">
        <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">{section.title}</h2>
        <div className="mt-10 space-y-12">
          {section.groups.map((group) => (
            <div key={group.title} className="animate-fade-up" style={reveal(80)}>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                {group.title}
              </h3>
              <dl className="mt-4 divide-y divide-border/70 border-t border-border/70">
                {group.rows.map(([label, value]) => (
                  <div
                    key={`${group.title}-${label}-${value}`}
                    className="grid grid-cols-1 gap-1 py-4 sm:grid-cols-[220px_1fr] sm:gap-6"
                  >
                    <dt className="text-sm text-muted-foreground">{label}</dt>
                    <dd className="text-sm text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">{section.note}</p>
      </div>
    </SectionShell>
  );
}

export function ProductCta({ section }: { section: CtaSection }) {
  return (
    <SectionShell border={false}>
      <div className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex flex-col items-start gap-6 rounded-3xl border border-border bg-card/50 p-8 animate-fade-up sm:flex-row sm:items-center sm:justify-between sm:p-12">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">{section.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{section.body}</p>
            <a
              href={section.compareHref}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <ExternalLink className="h-3 w-3" />
              {section.compareLabel}
            </a>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-11 rounded-lg px-6 font-medium">
              <a href={section.contactHref}>
                {section.contactLabel}
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-11 rounded-lg px-6 font-medium"
            >
              <a href={section.distributorHref}>{section.distributorLabel}</a>
            </Button>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
